import React, { useState, useMemo } from 'react';
import { Text } from 'src/core/forms';
import * as Select from '@radix-ui/react-select';
import { toString } from 'es-toolkit/compat';
import classnames from 'classnames';

import classes from './Dropdown.scss';

export interface Option {
    name: string;
    value: string;
    image?: string;
    swatch?: string;
    price?: string;
    subtitle?: string;
}

type Props = {
    label: string;
    options: Option[];
    field: string;
    onChange(...value: any);
    initialValue?: any;
    currentValue?: string | number;
    showOnlyImage?: boolean;
    short?: boolean;
    onOpenChange?(open: boolean): void;
};

export const Dropdown: React.FC<Props> = ({
    label,
    options,
    field,
    onChange,
    currentValue,
    initialValue,
    showOnlyImage,
    short = false,
    onOpenChange,
}) => {
    let computedCurrentValue = initialValue;
    if (typeof currentValue !== undefined) computedCurrentValue = toString(currentValue);

    const [internalValue, setInternalValue] = useState<string>(initialValue || computedCurrentValue);

    const effectiveValue = initialValue ? internalValue : computedCurrentValue;

    const optionsByValue = useMemo(
        () =>
            options.reduce<Record<string, Option>>((acc, item) => {
                acc[item.value] = item;
                return acc;
            }, {}),
        [options],
    );

    const handleChange = (newValue: string) => {
        // Radix's hidden native <select> reports '' when the value changes before its options render
        if (!optionsByValue[newValue]) return;
        setInternalValue(newValue);
        onChange(newValue);
    };

    const currentOption = optionsByValue[effectiveValue];
    const showSwatch = currentOption?.swatch;
    const showImg = currentOption?.image;

    return (
        <>
            <label>{label}</label>
            <Select.Root
                value={initialValue ? undefined : computedCurrentValue}
                defaultValue={initialValue || computedCurrentValue}
                onValueChange={handleChange}
                onOpenChange={onOpenChange}
                disabled={options.length <= 1}
            >
                <div className={classnames(classes.dropdownContainer, 'listbox-input', { [classes.short]: short })}>
                    <Select.Trigger className={classnames(classes.selectTrigger, 'listbox-button')}>
                        <span data-value={effectiveValue} style={{ display: 'flex' }}>
                            {showSwatch && (
                                <span
                                    data-testid={`swatch-${effectiveValue}`}
                                    style={{
                                        display: 'inline-block',
                                        borderRadius: '50%',
                                        width: '14px',
                                        height: '14px',
                                        marginRight: '14px',
                                        marginTop: '2px',
                                        backgroundColor: currentOption.swatch,
                                    }}
                                />
                            )}
                            <div className="dropdownValueContainer">
                                {showImg && (
                                    <img
                                        data-testid={`image-${effectiveValue}`}
                                        className={showOnlyImage ? classes.fullImage : classes.imageSwatch}
                                        src={currentOption.image}
                                        alt=""
                                    />
                                )}
                                {!showOnlyImage && (
                                    <>
                                        <span className={classes.dropdownTitle}>
                                            <Select.Value>{currentOption?.name}</Select.Value>
                                            <br />
                                            {currentOption?.subtitle && <b>{currentOption.subtitle}</b>}
                                        </span>
                                        {currentOption?.price && (
                                            <span className="dropdownValuePrice">{currentOption.price}</span>
                                        )}
                                    </>
                                )}
                            </div>
                        </span>
                        {options.length > 1 && (
                            <Select.Icon className={classnames(classes.selectArrow, 'listbox-arrow')}>
                                <div />
                            </Select.Icon>
                        )}
                    </Select.Trigger>
                    {options.length > 0 && (
                        <Select.Portal>
                            <Select.Content
                                className={classnames(classes.selectPopover, 'listbox-popover')}
                                position="popper"
                                side="bottom"
                                align="start"
                                sideOffset={0}
                                avoidCollisions={false}
                                style={{
                                    maxHeight: 240,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                                onWheel={(event) => event.stopPropagation()}
                            >
                                <Select.ScrollUpButton
                                    className={classnames(classes.scrollButton, classes.scrollButtonUp)}
                                />
                                <Select.Viewport
                                    className={classnames(classes.selectList, 'listbox-list')}
                                    style={{ maxHeight: 196, minHeight: 0, overflow: 'auto' }}
                                >
                                    {options.map((option) => (
                                        <Select.Item
                                            key={option.value}
                                            value={option.value}
                                            className={classnames(classes.selectOption, 'listbox-option')}
                                        >
                                            {option.image && (
                                                <>
                                                    <img
                                                        data-testid={`image-${option.name}`}
                                                        className={
                                                            showOnlyImage ? classes.fullImage : classes.imageSwatch
                                                        }
                                                        src={option.image}
                                                        alt=""
                                                    />
                                                    {!showOnlyImage && (
                                                        <span className={classes.optionName}>
                                                            <Select.ItemText>{option.name}</Select.ItemText>
                                                        </span>
                                                    )}
                                                    {showOnlyImage && <Select.ItemText>{option.name}</Select.ItemText>}
                                                </>
                                            )}
                                            {option.swatch && (
                                                <>
                                                    <span
                                                        data-testid={`swatch-${option.name}`}
                                                        style={{
                                                            display: 'inline-block',
                                                            borderRadius: '50%',
                                                            width: '14px',
                                                            height: '14px',
                                                            marginRight: '14px',
                                                            marginTop: '2px',
                                                            backgroundColor: option.swatch,
                                                        }}
                                                    />
                                                    <span className={classes.optionName}>
                                                        <Select.ItemText>{option.name}</Select.ItemText>
                                                    </span>
                                                </>
                                            )}
                                            {!option.image && !option.swatch && (
                                                <div className="dropdownValueContainer">
                                                    <span>
                                                        <span className={classes.optionName}>
                                                            <Select.ItemText>{option.name}</Select.ItemText>
                                                        </span>
                                                        <br />
                                                        {option.subtitle && <b>{option.subtitle}</b>}
                                                    </span>
                                                    {option.price && (
                                                        <span className="dropdownValuePrice">{option.price}</span>
                                                    )}
                                                </div>
                                            )}
                                        </Select.Item>
                                    ))}
                                </Select.Viewport>
                                <Select.ScrollDownButton
                                    className={classnames(classes.scrollButton, classes.scrollButtonDown)}
                                />
                            </Select.Content>
                        </Select.Portal>
                    )}
                </div>
            </Select.Root>
            <Text field={field} hidden />
        </>
    );
};
